import { Building2Icon, Plus, SearchLoop } from 'assets/icons';
import Button from 'components/button';
import StyledInput from 'components/styledInput/StyledInput';
import useText from 'hooks/useText';
import { useEffect, useRef, useState } from 'react';

// Accents and case aside: « carrefour » finds « CARREFOUR MARKET LORRAIN ».
const folded = (value) =>
  String(value ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();

/**
 * « Créer une mission » on a group: the button opens the group's companies
 * under a search bar, in a list of limited height that scrolls. Picking one
 * hands it to `onPick`. A click outside or Escape closes it. The question
 * « Pour quelle entreprise ? » is only read out (the dialog's label).
 * `companies`: { id, name, detail }.
 */
const CompanyPickMenu = ({ label, companies, onPick }) => {
  const { text } = useText('record');
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    inputRef.current?.focus();
    const closeOutside = (event) => {
      if (!ref.current?.contains(event.target)) setOpen(false);
    };
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', closeOutside);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('mousedown', closeOutside);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [open]);

  const toggle = () => {
    setSearch('');
    setOpen((value) => !value);
  };
  const query = folded(search.trim());
  const shown = companies
    .filter((company) => !query || folded(company.name).includes(query) || folded(company.detail).includes(query))
    .sort((a, b) => a.name.localeCompare(b.name, 'fr'));

  return (
    <div ref={ref} className="relative">
      <Button color="SECONDARY" size="SM" aria-expanded={open} aria-haspopup="dialog" onClick={toggle}>
        <Plus wh={18} color="currentColor" />
        <span>{label}</span>
      </Button>
      {open && (
        <div
          role="dialog"
          aria-label={text('pickCompanyTitle')}
          className="absolute right-0 top-full z-30 mt-2 flex w-80 flex-col gap-3 rounded-xl border border-gray-100 bg-white p-3 shadow-lg motion-safe:animate-contentIn"
        >
          <StyledInput
            type="text"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={text('pickCompanySearch')}
            imgBack={<SearchLoop wh={16} color="#6B7280" />}
            inputRef={inputRef}
          />
          <div className="-mx-1 max-h-72 overflow-y-auto">
            {!shown.length && <p className="px-2 py-3 text-sm text-gray-500">{text('pickCompanyEmpty')}</p>}
            {shown.map((company) => (
              <button
                key={company.id}
                type="button"
                onClick={() => {
                  setOpen(false);
                  onPick(company);
                }}
                className="flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left transition-colors hover:bg-gray-50 focus-visible:bg-gray-50 focus-visible:outline-none"
              >
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-gray-100 text-gray-900">
                  <Building2Icon wh={16} color="currentColor" />
                </span>
                <span className="min-w-0">
                  <span className="block truncate text-sm font-medium uppercase text-gray-900">{company.name}</span>
                  {company.detail && <span className="block truncate text-xs text-gray-600">{company.detail}</span>}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyPickMenu;
